'use client'

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions,
  getDoc,
  orderBy,
  runTransaction,
  setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';
import { useBudget } from './BudgetContext';
import { categorySymbols, defaultCategories } from './types/budget';

// Define types
export type Transaction = {
  id?: string;
  description: string;
  amount: number;
  date: Date;
  category: string;
  parentCategory: string; // Added this field
  type: 'income' | 'expense';
  accountId: string;
  budgetId: string;
  budgetItemId?: string; // Added this field
  receiptUrl?: string | null;
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

type TransactionInput = Omit<Transaction, 'id' | 'createdBy' | 'budgetId' | 'createdAt' | 'updatedAt' | 'date'>;

type TransactionState = {
  taxes: number | null | undefined;
  netWorth: number | null | undefined;
  transactions: Transaction[];
  totalRevenue: number;  // Added this
  totalSpending: number;
  groupedTransactions: Record<string, Transaction[]>;
  topCategories: Array<{ key: string; value: Transaction[] }>;
  month: number;
  year: number;
  period: string;
  loading: boolean;
  error: string | null;
};

type TransactionAction =
  | { type: 'ADD_TRANSACTION'; payload: Transaction }
  | { type: 'LOAD_TRANSACTIONS'; payload: Partial<TransactionState> }
  | { type: 'UPDATE_TRANSACTION'; payload: Transaction }
  | { type: 'DELETE_TRANSACTION'; payload: string }
  | { type: 'CHANGE_MONTH'; payload: { month: number; year: number } }
  | { type: 'CHANGE_PERIOD'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

type TransactionContextType = {
  state: TransactionState;
  addTransaction: (transaction: Omit<Transaction, 'id' | 'createdBy' | 'budgetId'>) => Promise<void>;
  loadTransactions: (filters?: {
    category?: string;
    accounts?: string[];
    minAmount?: number;
    maxAmount?: number;
    month?: number;
    year?: number;
    period?: string;
    startDate?: Date;  // Add this
    endDate?: Date;    // Add this
  }) => Promise<void>;
  updateTransaction: (transaction: Transaction, originalTransaction: Transaction) => Promise<void>;
  deleteTransaction: (transaction: Transaction) => Promise<void>;
  changeMonth: (month: number, year: number) => void;
  changePeriod: (period: string) => void;
  clearError: () => void;
};

const initialState: TransactionState = {
  transactions: [],
  totalSpending: 0,
  totalRevenue: 0,
  groupedTransactions: {},
  topCategories: [],
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  period: 'month',
  loading: false,
  error: null,
  netWorth: 0,
  taxes: 0,
};

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

function transactionReducer(state: TransactionState, action: TransactionAction): TransactionState {
  switch (action.type) {
    case 'ADD_TRANSACTION':
      return {
        ...state,
        transactions: [...state.transactions, action.payload],
        loading: false,
      };
    case 'LOAD_TRANSACTIONS':
      return {
        ...state,
        ...action.payload,
        loading: false,
      };
    case 'UPDATE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.map((t) =>
          t.id === action.payload.id ? action.payload : t
        ),
        loading: false,
      };
    case 'DELETE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.filter((t) => t.id !== action.payload),
        loading: false,
      };
    case 'CHANGE_MONTH':
      return {
        ...state,
        month: action.payload.month,
        year: action.payload.year,
        loading: true,
      };
    case 'CHANGE_PERIOD':
      return {
        ...state,
        period: action.payload,
        loading: true,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
}

const transactionConverter: FirestoreDataConverter<Transaction> = {
  toFirestore: (transaction: Transaction) => {
    return {
      ...transaction,
      date: Timestamp.fromDate(transaction.date),
      createdAt: transaction.createdAt ? Timestamp.fromDate(transaction.createdAt) : null,
      updatedAt: transaction.updatedAt ? Timestamp.fromDate(transaction.updatedAt) : null,
    };
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): Transaction => {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      date: data.date.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    } as Transaction;
  },
};

export function TransactionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(transactionReducer, initialState);
  const { state: budgetState } = useBudgetManager();
  const { state: accountState, updateAccount } = useAccount();
  const { loadBudgetItems, state: budgetState2 } = useBudget();

  // Add this helper function at the start of TransactionProvider
  const createMonthlyBudgetItems = async (
    userId: string,
    currentBudget: any,
    month: number,
    year: number
  ) => {
    const monthlyBudgetId = `${currentBudget.id}_${year}_${month}`;
    const budgetRef = doc(db, 'budgets', monthlyBudgetId);

    // Copy default or previous budget structure
    const budgetItems: Record<string, any> = {};

    // Add default categories
    for (const [category, items] of Object.entries(defaultCategories)) {
      budgetItems[category] = items.map(name => ({
        name,
        amount: 0,
        targetAmount: 0,
        spent: 0,
        category,
        date: new Date(year, month - 1),
        createdBy: userId,
        symbol: categorySymbols[name] || '📌'
      }));
    }

    await setDoc(budgetRef, {
      items: budgetItems,
      month,
      year,
      budgetId: currentBudget.id,
      createdBy: userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return budgetItems;
  };

  // Helper function to update account balance
  const updateAccountBalance = async (
    accountId: string,
    amount: number,
    type: 'income' | 'expense'
  ): Promise<{ newBalance: number; newAvailable: number }> => {
    try {
      // Input validation
      if (!accountId) throw new Error('Account ID is required');
      if (typeof amount !== 'number' || amount < 0) throw new Error('Invalid amount');
      if (!['income', 'expense'].includes(type)) throw new Error('Invalid transaction type');

      console.log('Updating account balance:', { accountId, amount, type });
      console.log('Current account state:', accountState.accounts);

      // Get fresh account data from Firestore
      const accountRef = doc(db, 'accounts', accountId);
      const accountSnap = await getDoc(accountRef);

      if (!accountSnap.exists()) {
        console.error(`Account ${accountId} not found in Firestore`);
        throw new Error(`Account with ID ${accountId} not found in database`);
      }

      const accountData = accountSnap.data();
      console.log('Retrieved account data:', accountData);

      if (!accountData) {
        throw new Error('Account data is empty');
      }

      // Calculate new balances
      const currentBalance = accountData.balance || 0;
      const currentAvailable = accountData.availableToBudget || 0;

      const newBalance = type === 'income'
        ? currentBalance + amount
        : currentBalance - amount;

      const newAvailable = type === 'income'
        ? currentAvailable + amount
        : currentAvailable - amount;

      // Validate results
      if (newBalance < 0) {
        console.warn('Warning: Account balance will be negative:', newBalance);
      }

      // Prepare update data
      const updateData = {
        balance: newBalance,
        availableToBudget: newAvailable,
        updatedAt: new Date()
      };

      console.log('Updating account with:', updateData);

      // Update Firestore
      await updateDoc(accountRef, updateData);

      // Update local state through AccountContext
      const account = accountState.accounts.find(a => a.id === accountId);
      if (account) {
        await updateAccount({
          ...account,
          ...updateData
        });
      } else {
        console.warn('Account not found in local state, but updated in Firestore');
      }

      return {
        newBalance,
        newAvailable
      };

    } catch (error) {
      console.error('Error in updateAccountBalance:', error);
      // Add additional error context
      const enhancedError = error instanceof Error
        ? new Error(`Failed to update account balance: ${error.message}`)
        : new Error('Failed to update account balance: Unknown error');

      // Preserve stack trace
      if (error instanceof Error) {
        enhancedError.stack = error.stack;
      }

      throw enhancedError;
    }
  };

  // Helper function to get monthly budget reference
  const getMonthlyBudgetRef = (budgetId: string, date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return doc(db, 'budgets', `${budgetId}_${year}_${month}`);
  };

  // Update the updateBudgetItemAmount function to work with monthly budgets
  const updateBudgetItemAmount = async (
    category: string,
    amount: number,
    type: 'income' | 'expense',
    date: Date,
    parentCategory?: string
  ) => {
    const monthlyBudgetRef = getMonthlyBudgetRef(
      budgetState.currentBudget!.id,
      date
    );
    const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

    if (!monthlyBudgetDoc.exists()) {
      // Create new monthly budget if it doesn't exist
      await createMonthlyBudgetItems(
        auth.currentUser!.uid,
        budgetState.currentBudget,
        date.getMonth() + 1,
        date.getFullYear()
      );
    }

    const budgetData = (await getDoc(monthlyBudgetRef)).data();

    if (type === 'income') {
      const updatedItems = {
        ...budgetData?.items,
        income: (budgetData?.items?.income || []).map((item: any) =>
          item.name === category
            ? {
              ...item,
              amount: (item.amount || 0) + amount,
              targetAmount: (item.targetAmount || 0) + amount
            }
            : item
        )
      };

      // If income category doesn't exist, create it
      if (!updatedItems.income.some((item: any) => item.name === category)) {
        updatedItems.income.push({
          name: category,
          amount: amount,
          targetAmount: amount,
          spent: 0,
          category: 'income',
          date: date,
          createdBy: auth.currentUser?.uid,
          symbol: '💰'
        });
      }

      await updateDoc(monthlyBudgetRef, { items: updatedItems });
    } else {
      // For expenses, update in the correct parent category
      if (!parentCategory) throw new Error('Parent category required for expenses');

      const updatedItems = {
        ...budgetData?.items,
        [parentCategory]: (budgetData?.items?.[parentCategory] || []).map((item: any) =>
          item.name === category
            ? {
              ...item,
              amount: (item.amount || 0) - amount,
              spent: (item.spent || 0) + amount
            }
            : item
        )
      };

      await updateDoc(monthlyBudgetRef, { items: updatedItems });
    }
  };

  const addTransaction = useCallback(async (transaction: Omit<Transaction, 'id' | 'createdBy' | 'budgetId' | 'createdAt' | 'updatedAt'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      if (!budgetState.currentBudget) throw new Error('No budget selected');

      // Get monthly budget reference
      const monthlyBudgetId = `${budgetState.currentBudget.id}_${transaction.date.getFullYear()}_${transaction.date.getMonth() + 1}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      // Create budget for this month if it doesn't exist
      if (!monthlyBudgetDoc.exists()) {
        await createMonthlyBudgetItems(
          user.uid,
          budgetState.currentBudget,
          transaction.date.getMonth() + 1,
          transaction.date.getFullYear()
        );
      }

      // Find the corresponding budget item
      const budgetData = (await getDoc(monthlyBudgetRef)).data();
      let budgetItemId: string | undefined;

      // Find budget item for any category
      const categoryItems = budgetData?.items?.[transaction.parentCategory] || [];
      const existingItem = categoryItems.find((item: any) => item.name === transaction.category);
      budgetItemId = existingItem ? `${transaction.parentCategory}_${transaction.category}` : undefined;

      const newTransaction: Transaction = {
        ...transaction,
        budgetId: budgetState.currentBudget.id,
        budgetItemId,
        createdBy: user.uid,
        receiptUrl: transaction.receiptUrl || null,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await runTransaction(db, async (firestoreTransaction) => {
        // Update account balance
        const { newBalance, newAvailable } = await updateAccountBalance(
          newTransaction.accountId,
          newTransaction.amount,
          newTransaction.type
        );

        // Update account document
        const accountRef = doc(db, 'accounts', newTransaction.accountId);
        firestoreTransaction.update(accountRef, {
          balance: newBalance,
          availableToBudget: newAvailable,
          updatedAt: new Date()
        });

        // Update budget items
        const currentItems = budgetData?.items || {};
        let updatedItems = { ...currentItems };

        const categoryItems = currentItems[transaction.parentCategory] || [];
        if (!categoryItems.length) {
          throw new Error(`Category ${transaction.parentCategory} not found`);
        }

        updatedItems[transaction.parentCategory] = categoryItems.map((item: any) => {
          if (item.name === transaction.category) {
            const currentSpent = item.spent || 0;
            const currentAmount = item.amount || 0;

            if (transaction.type === 'income') {
              const newAmount = currentAmount + newTransaction.amount;

              // If we have spent more than the new amount, reduce spent accordingly
              // but don't let it go below 0
              let newSpent = currentSpent;
              if (currentSpent > 0) {
                newSpent = Math.max(0, currentSpent - newTransaction.amount);
              }

              return {
                ...item,
                amount: newAmount,
                targetAmount: (item.targetAmount || 0) + newTransaction.amount,
                spent: newSpent
              };
            } else {
              // For expenses
              return {
                ...item,
                amount: currentAmount - newTransaction.amount,
                spent: currentSpent + newTransaction.amount
              };
            }
          }
          return item;
        });

        firestoreTransaction.update(monthlyBudgetRef, { items: updatedItems });

        // Add transaction document
        const transactionsCollection = collection(db, 'transactions');
        const newTransactionRef = doc(transactionsCollection);

        firestoreTransaction.set(newTransactionRef, newTransaction);

        dispatch({
          type: 'ADD_TRANSACTION',
          payload: { ...newTransaction, id: newTransactionRef.id },
        });
      });

      // Reload budget items to reflect changes
      await loadBudgetItems({
        month: transaction.date.getMonth() + 1,
        year: transaction.date.getFullYear()
      });

    } catch (error) {
      console.error('Error adding transaction:', error);
      dispatch({ type: 'SET_ERROR', payload: `Failed to add transaction: ${error}` });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [budgetState.currentBudget, updateAccountBalance, loadBudgetItems]);

  const updateTransaction = useCallback(async (
    transaction: Transaction,
    originalTransaction: Transaction
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      if (!transaction.id) throw new Error('Transaction ID is required for update');

      // Reverse the original transaction's effect
      await updateAccountBalance(
        originalTransaction.accountId,
        originalTransaction.amount,
        originalTransaction.type === 'income' ? 'expense' : 'income'
      );

      await updateBudgetItemAmount(
        originalTransaction.category,
        originalTransaction.amount,
        originalTransaction.type === 'income' ? 'expense' : 'income',
        new Date(originalTransaction.date.getFullYear(), originalTransaction.date.getMonth()),
        originalTransaction.parentCategory
      );

      // Apply the new transaction's effect
      await updateAccountBalance(
        transaction.accountId,
        transaction.amount,
        transaction.type
      );

      await updateBudgetItemAmount(
        transaction.category,
        transaction.amount,
        transaction.type,
        new Date(transaction.date.getFullYear(), transaction.date.getMonth()),
        transaction.parentCategory
      );

      const { id, ...updateData } = transaction;
      const transactionRef = doc(db, 'transactions', id).withConverter(transactionConverter);
      await updateDoc(transactionRef, updateData);

      dispatch({ type: 'UPDATE_TRANSACTION', payload: transaction });
    } catch (error) {
      console.error('Error updating transaction:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update transaction' });
    }
  }, []);

  const loadTransactions = useCallback(async (filters?: {
    category?: string;
    accounts?: string[];
    minAmount?: number;
    maxAmount?: number;
    month?: number;
    year?: number;
    period?: string;
    startDate?: Date;
    endDate?: Date;
  }) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
  
      if (!budgetState.currentBudget) throw new Error('No budget selected');
  
      const { month = state.month, year = state.year, period = state.period } = filters || {};
  
      let startDate: Date;
      let endDate: Date = filters?.endDate || new Date();
  
      if (filters?.startDate && filters?.endDate) {
        // Use provided date range if available
        startDate = filters.startDate;
        endDate = filters.endDate;
      } else {
        // Otherwise calculate based on period
        switch (period) {
          case 'weekly':
            startDate = new Date(year, month - 1, endDate.getDate() - 6);
            break;
          case 'monthly':
            startDate = new Date(year, month - 1, 1);
            break;
          case '2months':
            startDate = new Date(year, month - 2, 1);
            break;
          case '3months':
            startDate = new Date(year, month - 3, 1);
            break;
          case '6months':
            startDate = new Date(year, month - 6, 1);
            break;
          case '9months':
            startDate = new Date(year, month - 9, 1);
            break;
          case 'yearly':
            startDate = new Date(year, 0, 1);
            endDate = new Date(year, 11, 31);
            break;
          default:
            startDate = new Date(year, month - 1, 1);
            endDate = new Date(year, month, 0); // Last day of the month
        }
      }
  
      let q = query(
        collection(db, 'transactions'),
        where('createdBy', '==', user.uid),
        where('budgetId', '==', budgetState.currentBudget.id),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'asc')
      ).withConverter(transactionConverter);
  
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }
  
      if (filters?.accounts && filters.accounts.length > 0) {
        q = query(q, where('accountId', 'in', filters.accounts));
      }
  
      const querySnapshot = await getDocs(q);
      const transactions = querySnapshot.docs.map(doc => doc.data());
  
      const filteredTransactions = transactions.filter((t) => {
        const transactionDate = new Date(t.date);
        const isWithinPeriod = transactionDate >= startDate && transactionDate <= endDate;
  
        if (!isWithinPeriod) return false;
        if (filters?.minAmount && t.amount < filters.minAmount) return false;
        if (filters?.maxAmount && t.amount > filters.maxAmount) return false;
        return true;
      });
  
      // Calculate total revenue (income)
      const totalRevenue = filteredTransactions
        .filter((t) => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);
  
      // Calculate total spending (expenses)
      const totalSpending = filteredTransactions
        .filter((t) => t.type === 'expense')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  
      // Group transactions by category
      const groupedTransactions = filteredTransactions.reduce((acc, t) => {
        if (t.type === 'expense') {
          if (!acc[t.category]) acc[t.category] = [];
          acc[t.category].push(t);
        }
        return acc;
      }, {} as Record<string, Transaction[]>);
  
      // Calculate top spending categories
      const topCategories = Object.entries(groupedTransactions)
        .sort((a, b) =>
          b[1].reduce((sum, t) => sum + Math.abs(t.amount), 0) -
          a[1].reduce((sum, t) => sum + Math.abs(t.amount), 0)
        )
        .slice(0, 5)
        .map(([key, value]) => ({ key, value }));
  
      // Calculate net worth
      const totalAssets = accountState.accounts.reduce((sum, account) => {
        return sum + account.balance;
      }, 0);
  
      // Calculate estimated taxes
      const estimatedTaxes = totalRevenue * 0.25; // 25% tax rate for example
  
      dispatch({
        type: 'LOAD_TRANSACTIONS',
        payload: {
          transactions: filteredTransactions,
          totalRevenue,
          totalSpending,
          groupedTransactions,
          topCategories,
          month,
          year,
          period,
          netWorth: totalAssets,
          taxes: estimatedTaxes
        },
      });
    } catch (error) {
      console.error('Error loading transactions:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load transactions' 
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.month, state.year, state.period, budgetState.currentBudget, accountState.accounts]);


  const deleteTransaction = useCallback(async (transaction: Transaction) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      if (!transaction.id) throw new Error('Transaction ID is required for deletion');
      if (!budgetState.currentBudget?.id) throw new Error('No current budget selected');

      // Get monthly budget reference
      const monthlyBudgetId = `${budgetState.currentBudget.id}_${transaction.date.getFullYear()}_${transaction.date.getMonth() + 1}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      if (!monthlyBudgetDoc.exists()) {
        throw new Error('Monthly budget not found');
      }

      const budgetData = monthlyBudgetDoc.data();

      await runTransaction(db, async (firestoreTransaction) => {
        // Reverse account balance change
        const accountRef = doc(db, 'accounts', transaction.accountId);
        const accountSnap = await getDoc(accountRef);

        if (!accountSnap.exists()) {
          throw new Error(`Account with ID ${transaction.accountId} not found`);
        }

        const accountData = accountSnap.data();
        if (!accountData) {
          throw new Error('Account data is empty');
        }

        const currentBalance = accountData.balance || 0;
        const currentAvailable = accountData.availableToBudget || 0;

        // Calculate new balances by reversing the transaction
        const newBalance = transaction.type === 'income'
          ? currentBalance - transaction.amount
          : currentBalance + transaction.amount;

        const newAvailable = transaction.type === 'income'
          ? currentAvailable - transaction.amount
          : currentAvailable + transaction.amount;

        // Update account
        firestoreTransaction.update(accountRef, {
          balance: newBalance,
          availableToBudget: newAvailable,
          updatedAt: new Date()
        });

        // Update budget items
        let updatedItems = { ...budgetData.items };

        if (transaction.type === 'income') {
          // Reverse income transaction
          if (updatedItems.income) {
            updatedItems.income = updatedItems.income.map((item: any) => {
              if (item.name === transaction.category) {
                return {
                  ...item,
                  amount: (item.amount || 0) - transaction.amount,
                  targetAmount: (item.targetAmount || 0) - transaction.amount
                };
              }
              return item;
            });
          }
        } else {
          // Reverse expense transaction
          const categoryItems = updatedItems[transaction.parentCategory];
          if (Array.isArray(categoryItems)) {
            updatedItems[transaction.parentCategory] = categoryItems.map((item: any) => {
              if (item.name === transaction.category) {
                return {
                  ...item,
                  amount: (item.amount || 0) + transaction.amount,
                  spent: (item.spent || 0) - transaction.amount
                };
              }
              return item;
            });
          }
        }

        // Update monthly budget
        firestoreTransaction.update(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: new Date()
        });

        // Delete transaction
        const transactionRef = doc(collection(db, 'transactions'), transaction.id);
        firestoreTransaction.delete(transactionRef);
      });

      // Update local state
      dispatch({ type: 'DELETE_TRANSACTION', payload: transaction.id });

      // Reload budget items to reflect changes
      await loadBudgetItems({
        month: transaction.date.getMonth() + 1,
        year: transaction.date.getFullYear()
      });

    } catch (error) {
      console.error('Error deleting transaction:', error);
      dispatch({ type: 'SET_ERROR', payload: `Failed to delete transaction: ${error instanceof Error ? error.message : 'Unknown error'}` });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [budgetState.currentBudget, loadBudgetItems]);


  const changeMonth = useCallback((month: number, year: number) => {
    dispatch({ type: 'CHANGE_MONTH', payload: { month, year } });
    loadTransactions({ month, year });
  }, [loadTransactions]);

  const changePeriod = useCallback((period: string) => {
    dispatch({ type: 'CHANGE_PERIOD', payload: period });
    loadTransactions({ period, month: state.month, year: state.year });
  }, [loadTransactions, state.month, state.year]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && budgetState.currentBudget) {
        loadTransactions();
      } else {
        dispatch({ type: 'LOAD_TRANSACTIONS', payload: initialState });
      }
    });

    return () => unsubscribe();
  }, [loadTransactions, budgetState.currentBudget]);

  useEffect(() => {
    if (budgetState.currentBudget) {
      loadTransactions();
    }
  }, [budgetState.currentBudget, loadTransactions]);

  // Calculate net worth when accounts or transactions change
  useEffect(() => {
    const calculateNetWorth = () => {
      const totalAssets = accountState.accounts.reduce((sum, account) => {
        return sum + account.balance;
      }, 0);

      dispatch({
        type: 'LOAD_TRANSACTIONS',
        payload: {
          ...state,
          netWorth: totalAssets
        }
      });
    };

    calculateNetWorth();
  }, [accountState.accounts, state.transactions]);

  // Calculate estimated taxes based on income transactions
  useEffect(() => {
    const calculateEstimatedTaxes = () => {
      const totalIncome = state.transactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);

      // Simple tax estimation - could be made more sophisticated
      const estimatedTaxes = totalIncome * 0.25; // 25% tax rate for example

      dispatch({
        type: 'LOAD_TRANSACTIONS',
        payload: {
          ...state,
          taxes: estimatedTaxes
        }
      });
    };

    calculateEstimatedTaxes();
  }, [state.transactions]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const contextValue = {
    state,
    addTransaction,
    loadTransactions,
    updateTransaction,
    deleteTransaction,
    changeMonth,
    changePeriod,
    clearError,
  };

  return (
    <TransactionContext.Provider value={contextValue}>
      {children}
    </TransactionContext.Provider>
  );
}

// Custom hook to use the transaction context
export function useTransaction(): TransactionContextType {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransaction must be used within a TransactionProvider');
  }
  return context;
}

// Helper type for handling transaction updates
export type TransactionUpdate = {
  oldTransaction: Transaction;
  newTransaction: Transaction;
};

// Utility functions for transaction calculations
export const calculateTransactionImpact = (transaction: Transaction): number => {
  return transaction.type === 'income' ? transaction.amount : -transaction.amount;
};

export const formatTransactionAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const getCategoryTotals = (transactions: Transaction[]): Record<string, number> => {
  return transactions.reduce((acc, transaction) => {
    if (transaction.type === 'expense') {
      acc[transaction.category] = (acc[transaction.category] || 0) + transaction.amount;
    }
    return acc;
  }, {} as Record<string, number>);
};

export const getMonthlyTotals = (transactions: Transaction[]): Record<string, number> => {
  return transactions.reduce((acc, transaction) => {
    const monthYear = new Date(transaction.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
    acc[monthYear] = (acc[monthYear] || 0) + calculateTransactionImpact(transaction);
    return acc;
  }, {} as Record<string, number>);
};

// Constants for transaction filtering and display
export const TRANSACTION_PERIODS = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  THREE_MONTHS: '3months',
  SIX_MONTHS: '6months',
  NINE_MONTHS: '9months',
  YEARLY: 'yearly'
} as const;

export const TRANSACTION_TYPES = {
  INCOME: 'income',
  EXPENSE: 'expense'
} as const;

// Type guards for transaction-related checks
export const isIncomeTransaction = (transaction: Transaction): boolean => {
  return transaction.type === TRANSACTION_TYPES.INCOME;
};

export const isExpenseTransaction = (transaction: Transaction): boolean => {
  return transaction.type === TRANSACTION_TYPES.EXPENSE;
};

export const isSameTransaction = (a: Transaction, b: Transaction): boolean => {
  return (
    a.id === b.id &&
    a.amount === b.amount &&
    a.category === b.category &&
    a.type === b.type &&
    a.accountId === b.accountId
  );
};